import { useCallback } from "react";
import {
  AspectRatio,
  Box,
  Button,
  Center,
  Heading,
  LinkBox,
  LinkOverlay,
  ListItem,
  Progress,
  Text,
} from "@chakra-ui/react";
import { useGetTransparency } from "utils/colorHooks";
import { getFileExtension } from "utils/files/utils";
import useDownloadBrandDocument from "utils/files/useDownloadBrandDocument";
import asyncVoidHandler from "utils/asyncVoidHandler";
import DocumentArchiveButton from "components/Core/Documents/DocumentArchiveButton";

type Props = {
  children?: React.ReactNode;
  isArchived?: boolean;
  isDisabled?: boolean;
  documentId?: string;
  filename: string;
};

export default function FolderDocumentCard({
  children,
  isDisabled,
  isArchived,
  documentId,
  filename,
}: Props) {
  const [downloadFile, { isLoading: loading }] = useDownloadBrandDocument();

  const getTransparency = useGetTransparency();

  const handleClick = useCallback(async () => {
    if (!documentId) throw new Error("No document ID available!");
    await downloadFile(documentId, filename);
  }, [documentId, filename, downloadFile]);

  const ext = getFileExtension(filename);

  return (
    <LinkBox
      as={ListItem}
      display="flex"
      flexFlow="column"
      justifyContent="space-between"
      backgroundColor={getTransparency("lightSoftBlue", 0.25)}
      borderColor={getTransparency("softBlue", 0.5)}
      color="lightSoftBlue"
      borderWidth={1}
      borderRadius={10}
      overflow="hidden"
      data-is-archived={isArchived}
      sx={{
        "&[data-is-archived=true]": {
          backgroundColor: "gray.100",
          borderColor: "gray.200",
          color: "gray.300",
        },
      }}
    >
      {children}

      {documentId && (
        <Box
          position="absolute"
          p={1}
          opacity={0}
          visibility="hidden"
          sx={{
            "li:hover &": {
              opacity: 1,
              visibility: "visible",
              transitionDelay: "0s",
            },
          }}
          transition="opacity 150ms linear, visibility 0s linear 150ms"
        >
          <DocumentArchiveButton
            brandDocumentId={documentId}
            isArchived={isArchived}
          />
        </Box>
      )}

      <AspectRatio ratio={1}>
        <Center>
          {ext && (
            <Heading
              size="sm"
              textTransform="uppercase"
              sx={{
                "[data-is-archived=true] &": {
                  textDecoration: "line-through",
                },
              }}
            >
              .{ext}
            </Heading>
          )}
        </Center>
      </AspectRatio>

      <LinkOverlay
        as={Button}
        size="sm"
        disabled={loading || isDisabled || !documentId}
        onClick={asyncVoidHandler(handleClick)}
        height="auto"
        fontWeight={400}
        color="magenta.veryDark"
        backgroundColor="white"
        whiteSpace="normal"
        padding="2"
        margin={0}
        borderRadius={0}
        textTransform="none"
        _hover={{ backgroundColor: getTransparency("lightSoftBlue", 0.4) }}
        _active={{ backgroundColor: "lightSoftBlue" }}
        _focus={{ boxShadow: "none" }}
      >
        <Text noOfLines={3}>{filename}</Text>
      </LinkOverlay>

      {loading && (
        <Progress
          isIndeterminate
          size="xs"
          colorScheme="brand"
          position="absolute"
          bottom={0}
          left={0}
          right={0}
        />
      )}
    </LinkBox>
  );
}
